<script>
import moment from "moment";
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "HRM",
      text1: "Attendance Report",
      clientAdmin: clientAdmin(),
      filter: {
        month: Number(moment().format('M')),
        year: Number(moment().format('YYYY')),
        branch_id: '',
        department_id: '',
        employee_id: '',
        status: '',
      },
      months: [
        { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
        { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
        { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
        { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' },
      ],
      years: [],
      statuses: [
        { id: 'present', name: 'Present' },
        { id: 'absent', name: 'Absent' },
        { id: 'late', name: 'Late' },
        { id: 'half_day', name: 'Half Day' },
        { id: 'on_leave', name: 'On Leave' },
        { id: 'holiday', name: 'Holiday' },
      ],
      summary: {},
      data: [],
      departments: [],
      employees: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },
    branches() {
      return useLeadMetaStore().branches || [];
    },
  },

  mounted() {
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.years.push({ id: y, name: String(y) });
    }
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv', ...this.filter };
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/hrms/reports/attendance?${query_string}`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `attendance-report-${this.filter.month}-${this.filter.year}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = { page, ...this.filter };
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/hrms/reports/attendance?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      await useLeadMetaStore().loadAll();
      try {
        const [deptRes, empRes] = await Promise.allSettled([
          axios.get('/hrms/departments/dropdown'),
          axios.get('/hrms/employees', { params: { per_page: 1000 } }),
        ]);
        this.departments = deptRes.status === 'fulfilled' ? deptRes.value.data.data : [];
        this.employees = empRes.status === 'fulfilled' ? empRes.value.data.data.data : [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.branch_id || this.filter.department_id || this.filter.employee_id || this.filter.status;
    },

    clearFilter() {
      this.filter.branch_id = '';
      this.filter.department_id = '';
      this.filter.employee_id = '';
      this.filter.status = '';
      this.filterReport();
    },

    statusBadgeClass(status) {
      const map = {
        present: 'bg-success-subtle text-success',
        absent: 'bg-danger-subtle text-danger',
        late: 'bg-warning-subtle text-warning',
        half_day: 'bg-info-subtle text-info',
        on_leave: 'bg-primary-subtle text-primary',
        holiday: 'bg-secondary-subtle text-secondary',
      };
      return map[status] || 'bg-secondary-subtle text-secondary';
    },

    employeeName(row) {
      const name = row.employee?.full_name?.trim();
      return name || row.employee?.code || '-';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    changePctClass(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'text-success';
      if (num < 0) return 'text-danger';
      return 'text-muted';
    },

    changePctIcon(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'ti ti-trending-up';
      if (num < 0) return 'ti ti-trending-down';
      return 'ti ti-minus';
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Records</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_records }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Present</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ summary.total_present }}</span>
              <span v-if="summary.present_change_pct != null" :class="changePctClass(summary.present_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.present_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.present_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Absent</span>
              <span class="fw-bold text-danger" style="font-size:20px;">{{ summary.total_absent }}</span>
              <span v-if="summary.absent_change_pct != null" :class="changePctClass(summary.absent_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.absent_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.absent_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Attendance Rate</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.attendance_rate }}%</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Late</span>
              <span class="fw-bold text-warning" style="font-size:18px;">{{ summary.total_late }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Half Day</span>
              <span class="fw-bold text-info" style="font-size:18px;">{{ summary.total_half_day }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">On Leave</span>
              <span class="fw-bold text-primary" style="font-size:18px;">{{ summary.total_on_leave }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Holiday</span>
              <span class="fw-bold text-secondary" style="font-size:18px;">{{ summary.total_holiday }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Working Hrs</span>
              <span class="fw-bold text-dark" style="font-size:18px;">{{ summary.avg_working_hours }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.month" :options="months" label="name" :reduce="m => m.id"
                placeholder="Month" @update:modelValue="filterReport()" style="min-width: 140px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.year" :options="years" label="name" :reduce="y => y.id"
                placeholder="Year" @update:modelValue="filterReport()" style="min-width: 120px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.branch_id" :options="branches" label="name" :reduce="b => b.id"
                placeholder="Branch" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.department_id" :options="departments" label="name" :reduce="d => d.id"
                placeholder="Department" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.employee_id" :options="employees" label="full_name" :reduce="e => e.id"
                placeholder="Employee" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.status" :options="statuses" label="name" :reduce="s => s.id"
                placeholder="Status" :clearable="true" @update:modelValue="filterReport()" style="min-width: 160px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Working Hours</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>
                      <strong class="text-dark">{{ employeeName(row) }}</strong>
                      <small class="text-muted d-block" style="font-size:11px;">{{ row.employee?.code || '' }}</small>
                    </td>
                    <td>{{ row.employee?.department?.name || '-' }}</td>
                    <td>{{ formatDate(row.date) }}</td>
                    <td>
                      <span class="badge rounded-pill" :class="statusBadgeClass(row.status)">{{ row.status }}</span>
                    </td>
                    <td>{{ formatTime(row.punch_in) }}</td>
                    <td>{{ formatTime(row.punch_out) }}</td>
                    <td>{{ row.working_hours || '-' }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>